<template>
  <v-row no-gutters class="fill-height flex-column">
    <v-col v-if="isFetched">
      <card-title-row
        :straightTitle="additionalModuleName"
        :has-back-button="false"
        :class="isCardInfoPrint ? 'pb-4' : 'py-4'"
      >
        <template #buttons>
          <v-col :class="{ 'no-print': isCardInfoPrint }">
            <base-rounded-button
              icon="mdi-printer"
              size="x-small"
              :tooltip="$i18n.t('button.printAnalysisResult')"
              @click="$root.$emit('printCardInfo')"
            />
          </v-col>
        </template>
      </card-title-row>
      <card-title-row
        class="pb-1"
        translation-path="sidebar.parcelReport.basicInfos"
        :is-subtitle="true"
        :amount="2"
        :is-plural="true"
      />
      <sidebar-row>
        <v-row v-for="[key, value] in Object.entries(infos)" :key="key" no-gutters class="text-left">
          <sidebar-row v-if="value" :has-icon-column="false" class="my-1 text-subtitle-2">
            <span class="info-header-name">{{ $i18n.t(`sidebar.parcelReport.${key}`) }}:</span> {{ value }}
          </sidebar-row>
        </v-row>
        <v-divider class="my-5"></v-divider>
      </sidebar-row>
      <card-title-row
        class="pb-1"
        translation-path="sidebar.parcelReport.collisions"
        :is-subtitle="true"
        :amount="2"
        :is-plural="true"
      />
      <sidebar-row class="pr-5 mb-7">
        <v-list v-if="Object.keys(collisions).length > 0" class="py-0">
          <template v-for="(features, layer_id) in collisions">
            <template v-for="data in features">
              <v-tooltip bottom :key="`${layer_id}-${data.feature_id}`">
                <template v-slot:activator="{ on }">
                  <v-list-item
                    v-on="on"
                    class="hover-list-element"
                    :style="`break-inside: ${isCardInfoPrint ? 'avoid' : 'auto'}`"
                  >
                    <v-list-item-content class="pa-0">
                      <v-list-item-title>
                        <v-subheader
                          class="pl-0 font--text text-body-3 text-truncate text-left"
                          style="height: auto; display: block"
                        >
                          {{ collisionName(layer_id, data.feature_id) }}
                        </v-subheader>
                        <italic-subheader
                          v-if="data.area"
                          style="display: block"
                          class="text-truncate text-left"
                          :straight-text="`${$i18n.t(`sidebar.parcelReport.areaOnParcel`)}: ${data.area.toFixed(2)}m2`"
                        >
                        </italic-subheader>
                        <italic-subheader
                          v-if="data.length"
                          style="display: block"
                          class="text-truncate text-left"
                          :straight-text="
                            `${$i18n.t(`sidebar.parcelReport.lengthOnParcel`)}: ${data.length.toFixed(2)}m`
                          "
                        >
                        </italic-subheader>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action :class="{ 'no-print': isCardInfoPrint }">
                      <v-icon @click="zoomToFeature(data.geometry)" color="rgba(0,0,0,.7)" size="19px">
                        mdi-magnify
                      </v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </template>
                {{ collisionName(layer_id, data.feature_id) }}
              </v-tooltip>
            </template>
          </template>
        </v-list>
        <italic-subheader
          v-else
          style="display: block"
          class="text-truncate text-left"
          :straight-text="$i18n.t(`sidebar.parcelReport.noCollisions`)"
        >
        </italic-subheader>
      </sidebar-row>
    </v-col>
    <v-col v-else>
      <main-loader class="pt-10" />
    </v-col>
  </v-row>
</template>

<script>
import { get, call } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import measurements from '@/mixins/map/measurements';
import WKT from 'ol/format/WKT';
import { GeoJSON } from 'ol/format';
import { formatArea, formatLength } from '@/assets/js/mapUtils';

export default {
  name: 'ParcelReport',
  mixins: [measurements],
  components: {
    MainLoader,
    SidebarRow,
    CardTitleRow,
    ItalicSubheader,
  },
  data: () => ({
    collisions: {},
    isFetched: false,
    infos: {
      number: null,
      voivodeship: null,
      county: null,
      commune: null,
      area: null,
      circuit: null,
    },
  }),
  computed: {
    dataSources: get('layers/metadata'),
    additionalModuleName: get('admin/additionalModules@PARCELS_REPORT.verbose_name'),
    parcelReportCoordinates: get('search/parcelReportCoordinates'),
    parcelSettings: get('admin/modulesMapping@parcels'),
    settingsParcelsProvider: get('admin/settingsValues@parcels_provider'),
    projectElements: get('layers/project@layers'),
    isCardInfoPrint: get('tools/toolStatus@isCardInfoPrintActive'),
    visibleLayersIds() {
      return this.projectLayers.filter(layer => layer.visible).map(layer => layer.layer_id);
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  methods: {
    searchUldkParcelByXY: call('search/searchUldkParcelByXY'),
    getParcelCollisions: call('search/getParcelCollisions'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    collisionName(layer_id, feature_id) {
      return `${this.projectLayers.find(layer => parseInt(layer.layer_id) === parseInt(layer_id)).name}: ${feature_id}`;
    },
    zoomToFeature(feature_geometry) {
      this.$root.$emit('createGeomAndFitView', feature_geometry);
    },
    getParcelGeometry(geometryString, type, projectionEpsg) {
      let geom;
      if (type === 'wkt') {
        geom = new WKT().readGeometry(geometryString, {
          featureProjection: this.$_config.defaultEpsg || 'EPSG:4326',
          dataProjection: projectionEpsg || 'EPSG:4326',
        });
      } else {
        geom = new GeoJSON().readGeometry(geometryString, {
          featureProjection: this.$_config.defaultEpsg || 'EPSG:4326',
          dataProjection: projectionEpsg || 'EPSG:4326',
        });
      }
      return geom;
    },
    getParcelArea(geom, measurement) {
      return measurement === 'area' ? formatArea(geom) : formatLength(geom);
    },
    async setParcel(coords) {
      this.isFetched = false;
      let projectionEpsg = this.$_config.defaultEpsg;
      if (this.settingsParcelsProvider === 'uldk') {
        const r = await this.searchUldkParcelByXY({
          coords: {
            x: coords[0],
            y: coords[1],
            epsg: this.$_config.defaultEpsg.split(':')[1],
            params: ['geom_wkt', 'numer', 'teryt', 'voivodeship', 'commune', 'county', 'region'],
          },
        });
        if (r !== null && r.error) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.notFoundPlotWithError', { error: r.error }),
          });
          this.$root.$emit('removeParcelReportLayer');
          this.$router.push({
            name: 'map',
          });
          return;
        }
        let wkt = r.geom_wkt;
        if (wkt.indexOf(';') >= 0) {
          projectionEpsg = `EPSG:${wkt.split(';')[0].split('=')[1]}`;
          wkt = wkt.split(';')[1];
        }
        const parcelGeometry = this.getParcelGeometry(wkt, 'wkt', projectionEpsg);
        const polygon = {
          type: 'Polygon',
          crs: {
            type: 'name',
            properties: {
              name: this.$_config.defaultEpsg || 'EPSG:4326',
            },
          },
          coordinates: parcelGeometry.getCoordinates(),
        };
        if (this.visibleLayersIds.length !== 0) {
          const collisions = await this.getParcelCollisions({
            geometry: polygon,
            layers: this.visibleLayersIds,
            params: {
              with_geometry: true,
            },
          });
          this.collisions = collisions;
        } else {
          this.collisions = [];
        }
        this.$root.$emit('addParcelReportLayer', parcelGeometry);
        this.infos = {
          number: r.teryt,
          voivodeship: r.voivodeship ? r.voivodeship : 'null',
          county: r.county ? r.county : 'null',
          commune: r.commune ? r.commune : 'null',
          region: r.region ? r.region : 'null',
          area: this.getParcelArea(parcelGeometry, 'area'),
          circuit: this.getParcelArea(parcelGeometry, 'length'),
        };
        this.isFetched = true;
      } else {
        const point = {
          type: 'Point',
          crs: {
            type: 'name',
            properties: {
              name: this.$_config.defaultEpsg || 'EPSG:4326',
            },
          },
          coordinates: coords,
        };
        const data = await this.getDataSourceFeatures({
          dataSource: this.parcelSettings.parcels_datasource,
          filters: {
            '%SPATIALLY_INTERSECTS': {
              [`${this.parcelSettings.parcels_datasource}.${this.dataSources[this.parcelSettings.parcels_datasource]
                ?.attributes_schema.geometry_name ?? 'geom'}`]: [point],
            },
          },
        });
        if (data.features.length === 0) {
          this.$store.set('snackbar/PUSH_MESSAGE!', {
            message: this.$i18n.t('snackbar.notFoundPlot'),
          });
          this.$root.$emit('removeParcelReportLayer');
          this.$router.push({
            name: 'map',
          });
          return;
        }
        const parcelGeometry = this.getParcelGeometry(data.features[0].geometry, 'geojson', projectionEpsg);
        const polygon = {
          ...data.features[0].geometry,
          crs: {
            type: 'name',
            properties: {
              name: this.$_config.defaultEpsg || 'EPSG:3857',
            },
          },
        };
        if (this.visibleLayersIds.length !== 0) {
          const collisions = await this.getParcelCollisions({
            geometry: polygon,
            layers: this.visibleLayersIds,
            params: {
              with_geometry: true,
            },
          });
          this.collisions = collisions;
        } else {
          this.collisions = [];
        }
        this.$root.$emit('addParcelReportLayer', parcelGeometry);
        this.isFetched = true;
        this.infos = {
          number: data.features[0].properties[this.parcelSettings.attribute_number],
          voivodeship: null,
          county: null,
          commune: data.features[0].properties[this.parcelSettings.attribute_community]
            ? data.features[0].properties[this.parcelSettings.attribute_community]
            : 'null',
          region: data.features[0].properties[this.parcelSettings.attribute_region]
            ? data.features[0].properties[this.parcelSettings.attribute_region]
            : 'null',
          area: this.getParcelArea(parcelGeometry, 'area'),
          circuit: this.getParcelArea(parcelGeometry, 'length'),
        };
      }
    },
    onCoordinatesChange(coords) {
      this.setParcel(coords);
    },
  },
  watch: {
    parcelReportCoordinates(nV) {
      if (nV.length > 0) {
        this.onCoordinatesChange(nV);
      }
    },
  },
  created() {
    if (this.parcelReportCoordinates.length !== 0) {
      this.onCoordinatesChange(this.parcelReportCoordinates);
    } else {
      this.$router.push({
        name: 'map',
      });
    }
  },
  beforeDestroy() {
    this.$root.$emit('removeParcelReportLayer');
    this.$store.set('search/SET_PARCEL_REPORT_COORDINATES!', []);
  },
};
</script>

<style lang="scss" scoped>
.info-header-name {
  opacity: 0.6;
}
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
</style>
